import { parseISO } from "date-fns";
import { useMemo } from "react";

import type { FeelingAtHomeDataMonthlyDto } from "@/api/types";
import { ChartLegend } from "@/components/Charts/ChartLegend";
import { ChartTooltip } from "@/components/Charts/ChartTooltip";
import { dataColors } from "@/components/Charts/theme";
import { parseChartDate } from "@/components/Charts/util";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { Stack } from "@/design-system/Stack";
import { isDefined } from "@/helpers/util";
import { useTranslation } from "@/translations";
import {
  type ActiveDotProps,
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  ResponsiveContainer,
  Tooltip,
  type TooltipContentProps,
  XAxis,
  YAxis,
} from "@/ui-adapters/Recharts";

type FahWidgetTrendChartProps = {
  data: FeelingAtHomeDataMonthlyDto[];
};

const MAX_Y = 10;
const MIN_Y = 0;
const MARGIN_Y = 2;

export const FahWidgetTrendChart = ({ data }: FahWidgetTrendChartProps): React.ReactNode => {
  const { t, i18n } = useTranslation();

  const chartData = useMemo(
    () =>
      data.map((stat) => ({
        x: parseChartDate({
          month: parseISO(stat.month).getMonth() + 1,
          year: parseISO(stat.month).getFullYear(),
        }).toISOString(),
        primary: stat.totalFeelingAtHomeScore?.rating ?? null,
        benchmark: stat.benchmarkFeelingAtHomeScore?.rating ?? null,
      })),
    [data],
  );

  const allYValues = useMemo(
    () => chartData.flatMap((d) => [d.primary, d.benchmark]).filter((y): y is number => y !== null),
    [chartData],
  );
  const minYDomain = allYValues.length > 0 ? Math.floor(Math.min(...allYValues)) : MIN_Y;
  const maxYDomain = allYValues.length > 0 ? Math.ceil(Math.max(...allYValues)) : MAX_Y;

  return (
    <Stack gap="md" align="center">
      <figure className="aspect-6/5 w-full @md:aspect-3/1">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
            <defs>
              <linearGradient id="area-background" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={dataColors.primary} stopOpacity={0.2} />
                <stop offset="100%" stopColor={dataColors.primary} stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="var(--color-border-default)" vertical={false} strokeWidth={0.5} />
            <XAxis
              dataKey="x"
              tickFormatter={(value) => formatDate(i18n, "monthShort", value)}
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: "var(--text-overline-size)" }}
              tickMargin={12}
              interval="preserveStartEnd"
            />
            <YAxis
              domain={[Math.max(minYDomain - MARGIN_Y, MIN_Y), Math.min(maxYDomain + MARGIN_Y, MAX_Y)]}
              tickLine={false}
              axisLine={false}
              ticks={[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]}
              tick={{ fontSize: "var(--text-overline-size)" }}
              tickMargin={8}
              width={24}
            />
            <Tooltip cursor={{ stroke: dataColors.axis }} content={<CustomTooltip />} />
            <Line
              type="linear"
              dataKey="benchmark"
              stroke={dataColors.benchmark}
              strokeWidth={2}
              strokeDasharray="2 2"
              dot={false}
              activeDot={false}
              connectNulls={false}
            />
            <Area
              type="linear"
              dataKey="primary"
              stroke={dataColors.primary}
              fill="url(#area-background)"
              strokeWidth={2}
              connectNulls={false}
              activeDot={<CustomizedActiveDot />}
            />
          </AreaChart>
        </ResponsiveContainer>
      </figure>

      <ChartLegend
        items={[
          {
            label: t("component.analytics-widget.fah.graph.legend.primary"),
            color: dataColors.primary,
          },
          {
            label: t("common.label.aop-benchmark"),
            color: dataColors.benchmark,
          },
        ]}
      />
    </Stack>
  );
};

function CustomizedActiveDot(props: Partial<ActiveDotProps>) {
  const { cx, cy, payload } = props;
  if (!isDefined(payload.primary)) {
    return null;
  }

  return (
    <>
      <circle cx={cx} cy={cy} r={8} fill="transparent" strokeWidth={2} stroke={dataColors.primary} opacity={0.2} />
      <circle
        cx={cx}
        cy={cy}
        r={4}
        fill="var(--color-background-default)"
        strokeWidth={2}
        stroke={dataColors.primary}
      />
    </>
  );
}

function CustomTooltip({ active, payload, label }: Partial<TooltipContentProps<number, number>>) {
  const { t, i18n } = useTranslation();

  if (!active || !payload?.length) {
    return null;
  }

  const primaryPayload = payload.find((p) => p.dataKey === "primary");
  const benchmarkPayload = payload.find((p) => p.dataKey === "benchmark");

  if ((!isDefined(primaryPayload?.value) && !isDefined(benchmarkPayload?.value)) || !label) {
    return null;
  }

  return (
    <ChartTooltip>
      <Stack gap="xs">
        {isDefined(primaryPayload?.value) && typeof primaryPayload?.value === "number" && (
          <span className="text-caption-emphasis">
            {t("component.analytics-widget.fah.graph.tooltip", {
              score: primaryPayload.value.toFixed(1),
            })}
          </span>
        )}
        {isDefined(benchmarkPayload?.value) && typeof benchmarkPayload?.value === "number" && (
          <span className="text-caption-emphasis">
            {t("component.analytics-widget.fah.graph.tooltip.benchmark", {
              score: benchmarkPayload.value.toFixed(1),
            })}
          </span>
        )}
        <span className="text-caption">{formatDate(i18n, "monthYearShort", label)}</span>
      </Stack>
    </ChartTooltip>
  );
}
